<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';

import DeliveryShipmentSummary from '@/components/checkout/steps/delivery/DeliveryShipmentSummary.vue';
import { useCart } from '@/composables/useCart';
import { useDelivery } from '@/composables/useDelivery';
import { isGreetingCardLineItem, Shipment } from '@/utils/cart';

defineProps<{
  isLoadingShippingOffers?: boolean;
}>();
defineEmits<{ edit: [setGiftMessage?: boolean] }>();

const store = useStore();
const { lineItems } = useCart(store);
const { hasMultipleShipments, isPickup, standardShipments } = useDelivery(store);

const hasGreetingCards = computed(() => lineItems.value.some(isGreetingCardLineItem));

const buildShippingOfferFromShipment = (shipment: Shipment) => {
  const customFields = shipment.customFields ?? {};

  return {
    earliestArrivalOn: customFields.earliestArrivalOn,
    latestArrivalOn: customFields.latestArrivalOn,
    price: customFields.price,
    name: customFields.shipmentPickupShippingOption,
    shipmentPickup: {
      carrierCode: customFields.shipmentPickupCarrierCode,
      carrierPickup: customFields.shipmentPickupCarrierPickup,
    },
  };
};
</script>

<template>
  <div>
    <div class="divide-y divide-gray-200 divide-solid">
      <div v-for="shipment in standardShipments" :key="shipment.key">
        <div v-if="hasMultipleShipments" class="mt-3 font-semibold md:mt-6">
          Address {{ shipment.multishipSerial }}:
          {{ shipment.address.name }}
          <template v-if="shipment.address.city">in {{ shipment.address.city }}</template>
        </div>
        <DeliveryShipmentSummary
          class="mt-3"
          :giftMessage="!!shipment.packingSlipMessage && !hasGreetingCards"
          highlight
          :hasMultipleShipments
          :isDigital="!shipment.address.street1 && !isPickup"
          :isLoading="isLoadingShippingOffers"
          :isPickup
          :shippingOffer="buildShippingOfferFromShipment(shipment)"
          @edit="$emit('edit')"
        />
      </div>
    </div>
  </div>
</template>
